import styled from 'styled-components'
import { Button, Checkbox, P, Wrapper } from '@farewill/ui'
import { FONT, GTR, COLOR } from '@farewill/ui/tokens'
import { useDispatch } from 'react-redux'

import { hideModal } from 'state/actions'
import { formatCurrency } from 'lib/formatting/currency'
import { useState } from 'react'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
`

const StyledList = styled.ol`
  list-style: numeric;
  margin-left: 20px;

  li {
    margin: 6px 0;
  }
`

const StyledErrorMessage = styled(P)`
  color: ${COLOR.STATE.ERROR};
  margin-top: ${GTR.XS};
  font-size: ${FONT.SIZE.S};
`

const StyledCancelButton = styled(Button)`
  font-weight: ${FONT.WEIGHT.REGULAR};
  text-decoration: underline;
  align-self: center;
  margin-top: ${GTR.XS};

  &:hover {
    text-decoration: underline;
  }
`

type ModalProps = {
  config: {
    leftToPayInPence: number
    redeemPlan: () => Promise<void>
  }
}

const RedeemPlanModal = ({ config }: ModalProps) => {
  const [accidentalDeathSelected, setAccidentalDeathSelected] = useState(false)
  const [showError, setShowError] = useState(false)

  const outstandingBalance = formatCurrency({
    showPence: true,
    value: config.leftToPayInPence,
    valueInPence: true,
  })

  const dispatch = useDispatch()
  const hasOutstandingBalance = config.leftToPayInPence > 0

  const handleRedeemPlan = async () => {
    if (hasOutstandingBalance && !accidentalDeathSelected) {
      setShowError(true)
      return
    }
    setShowError(false)
    await config.redeemPlan()
    dispatch(hideModal())
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setAccidentalDeathSelected(checked)

    if (checked && showError) {
      setShowError(false)
    }
  }

  return (
    <Wrapper>
      <P>
        You are about to redeem the funeral plan. Before you continue, make sure
        that:
        <StyledList>
          <li>You have checked the funeral plan number</li>
          <li>The plan is completely paid for (or it’s an accidental death)</li>
          <li>
            The covered individual name matches the one given by the caller
          </li>
        </StyledList>
      </P>

      {hasOutstandingBalance && (
        <>
          <P>
            Outstanding Balance: <strong>{outstandingBalance}</strong>
          </P>
          <Checkbox
            label="This is an accidental death and the balance will not be taken"
            checked={accidentalDeathSelected}
            onChange={handleCheckboxChange}
          ></Checkbox>
          {showError && (
            <StyledErrorMessage>
              You must confirm this is an accidental death before redeeming the
              plan
            </StyledErrorMessage>
          )}
        </>
      )}

      <StyledWrapper>
        <Button.Primary type="button" onClick={handleRedeemPlan}>
          Continue
        </Button.Primary>
        <StyledCancelButton onClick={() => dispatch(hideModal())}>
          Cancel
        </StyledCancelButton>
      </StyledWrapper>
    </Wrapper>
  )
}

export default RedeemPlanModal
